import {
    useEffect,
    useMemo,
    useCallback,
    useRef,
    useState,
    type MouseEvent as ReactMouseEvent,
} from 'react';
import {
    ReactFlow,
    Background,
    type Node,
    type Edge,
    type NodeMouseHandler,
    type ReactFlowInstance,
    ReactFlowProvider,
} from '@xyflow/react';
import {CircleHelp} from 'lucide-react';
import '@xyflow/react/dist/style.css';
import {findHighlightPath, getLayoutedElements, treeToFlow} from './flowUtils';
import type {TreeNode} from '@/types';
import {useMindMapStore} from '@/store/mindMapStore.ts';
import {useAppStore} from '@/store/appStore';
import {NavBar} from '@/pages/mindmap/NavBar.tsx';
import {CategoryCardNode} from "@/pages/mindmap/CarNodes/CategoryCardNode/CategoryCardNode.tsx";
import {RegionNode} from "@/pages/mindmap/CarNodes/RegionNode.tsx";
import {PlanNode} from "@/pages/mindmap/CarNodes/PlanNode.tsx";
import {ProvinceNode} from "@/pages/mindmap/CarNodes/ProvinceNode.tsx";
import {SegmentNode} from "@/pages/mindmap/CarNodes/SegmentNode.tsx";
import {HospitalListNode} from "@/pages/mindmap/CarNodes/HospitalListNode.tsx";
import {SummaryNode} from "@/pages/mindmap/CarNodes/SummaryNode/SummaryNode.tsx";
import {PlatformNode} from "@/pages/mindmap/CarNodes/PlatformNode.tsx";

const nodeTypes = {
    Summary: SummaryNode,
    Platform: PlatformNode,
    Plan: PlanNode,
    category: CategoryCardNode,
    Region: RegionNode,
    Province: ProvinceNode,
    Segment: SegmentNode,
    hospitalList: HospitalListNode,
};

function MindMapFlowInner() {
    const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
    const nodeClickTimerRef = useRef<number | null>(null);
    const [navPath, setNavPath] = useState<{ id: string; name: string }[]>([]);
    const [isHelpVisible, setIsHelpVisible] = useState(false);

    // ---- Global store: batch ------------------------------------------------
    const selectBatch = useAppStore((s) => s.selectBatch);

    // ---- Zustand mind-map store ---------------------------------------------
    const data = useMindMapStore((s) => s.data);
    const loading = useMindMapStore((s) => s.loading);
    const expandedNodes = useMindMapStore((s) => s.expandedNodes);
    const showHighlightPath = useMindMapStore((s) => s.showHighlightPath);

    const fetchTreeData = useMindMapStore((s) => s.fetchTreeData);
    const toggleNode = useMindMapStore((s) => s.toggleNode);
    const collapseAll = useMindMapStore((s) => s.collapseAll);
    const toggleHighlightPath = useMindMapStore((s) => s.toggleHighlightPath);


    useEffect(() => {
        if (!selectBatch) return;
        fetchTreeData(selectBatch.id);
    }, [selectBatch, fetchTreeData]);

    // Set breadcrumb path when tree data arrives
    useEffect(() => {
        if (data?.path) setNavPath(data.path);
    }, [data]);

    useEffect(() => {
        return () => {
            if (nodeClickTimerRef.current !== null) {
                window.clearTimeout(nodeClickTimerRef.current);
            }
        };
    }, []);

    // ---- Derived state --------------------------------------------------------
    const highlightPath = useMemo(() => {
        if (!data || !showHighlightPath) return null;
        return findHighlightPath(data);
    }, [data, showHighlightPath]);

    const {nodes, edges} = useMemo(() => {
        if (!data || !selectBatch) return {nodes: [] as Node[], edges: [] as Edge[]};
        return treeToFlow(data, expandedNodes, showHighlightPath, highlightPath, selectBatch.id);
    }, [data, expandedNodes, showHighlightPath, highlightPath, selectBatch]);

    const {nodes: layoutedNodes, edges: layoutedEdges} = useMemo(() => {
        return getLayoutedElements(nodes, edges);
    }, [nodes, edges]);

    // ---- Node click handler ---------------------------------------------------
    const focusNode = useCallback((nodeId: string) => {
        setTimeout(() => {
            const instance = reactFlowInstance.current;
            const targetNode = instance?.getNode(nodeId);
            if (!instance || !targetNode) return;

            void instance.fitView({
                nodes: [targetNode],
                padding: 0.5,
                maxZoom: 1.2,
                duration: 400,
            });
        }, 100);
    }, []);

    const onNodeDoubleClick: NodeMouseHandler = useCallback(
        (_, node) => {
            if (nodeClickTimerRef.current !== null) {
                window.clearTimeout(nodeClickTimerRef.current);
                nodeClickTimerRef.current = null;
            }
            focusNode(node.id);
        },
        [focusNode],
    );

    const onNodeClick: NodeMouseHandler = useCallback(
        (_, node) => {
            if (nodeClickTimerRef.current !== null) {
                window.clearTimeout(nodeClickTimerRef.current);
            }

            nodeClickTimerRef.current = window.setTimeout(() => {
                nodeClickTimerRef.current = null;

                const nodeData = (node.data as any)?.node as TreeNode;
                if (!nodeData) return;

                // Update breadcrumb path
                if (nodeData.path) setNavPath(nodeData.path);

                // Determine if the node can expand
                const isProvinceOrSegment =
                    nodeData.type === 'Province' ||
                    nodeData.type === 'Segment' ||
                    nodeData.type?.toLowerCase() === 'province' ||
                    nodeData.type?.toLowerCase() === 'segment';
                const isTop100KA = nodeData.id === 'top100ka';
                const isGrowthPlan = nodeData.id?.endsWith('-growth-plan');
                const isVbpProvince =
                    nodeData.id?.includes('-vbp-province-') || nodeData.id?.includes('-nonvbp-province-');
                const needsHospitalList =
                    (isProvinceOrSegment && !isVbpProvince) || isTop100KA || isGrowthPlan;
                const canExpand =
                    (nodeData.children && nodeData.children.length > 0) || needsHospitalList;

                if (!canExpand) return;

                toggleNode(nodeData.id);

                // fitView after expand
                setTimeout(() => {
                    reactFlowInstance.current?.fitView({padding: 0.2, duration: 400});
                }, 100);
            }, 250);
        },
        [toggleNode],
    );

    // ---- Control handlers ------------------------------------------------------
    const handleFitView = () => reactFlowInstance.current?.fitView({padding: 0.2});

    const handlePaneClick = useCallback((event: ReactMouseEvent) => {
        if (event.detail === 2) {
            void reactFlowInstance.current?.zoomOut({duration: 300});
        }
    }, []);

    const handleCollapseAll = () => {
        collapseAll();
        setTimeout(() => reactFlowInstance.current?.fitView({padding: 0.2}), 100);
    };

    const handleFilterCritical = () => {
        toggleHighlightPath();
        setTimeout(() => reactFlowInstance.current?.fitView({padding: 0.2}), 100);
    };

    const handleFullscreen = () => {
        const isFs = document.body.classList.contains('fullscreen-mode');
        if (!isFs) {
            document.body.classList.add('fullscreen-mode');
            document.documentElement.requestFullscreen?.();
            setTimeout(() => reactFlowInstance.current?.fitView({padding: 0.1, duration: 300}), 200);
        } else {
            document.body.classList.remove('fullscreen-mode');
            document.exitFullscreen?.();
        }
    };

    // ---- Render ----------------------------------------------------------------
    if (loading) {
        return (
            <div
                style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <div className="spinner"/>
            </div>
        );
    }

    if (!data) {
        return (
            <div
                style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                }}
            >
                No data
            </div>
        );
    }

    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc'}}>
            <NavBar path={navPath}/>

            <div style={{flex: 1, position: 'relative'}}>
                <ReactFlowProvider>
                    <ReactFlow
                        nodes={layoutedNodes}
                        edges={layoutedEdges}
                        nodeTypes={nodeTypes}
                        onNodeClick={onNodeClick}
                        onNodeDoubleClick={onNodeDoubleClick}
                        onPaneClick={handlePaneClick}
                        onInit={(instance) => {
                            reactFlowInstance.current = instance;
                        }}
                        fitView
                        fitViewOptions={{padding: 0.2}}
                        minZoom={0.1}
                        maxZoom={2}
                        zoomOnDoubleClick={false}
                        proOptions={{hideAttribution: true}}
                    >
                        <Background color="#e2e8f0" gap={20}/>
                    </ReactFlow>
                </ReactFlowProvider>

                {isHelpVisible && (
                    <div
                        id="mindmap-help"
                        role="tooltip"
                        style={{padding: '10px'}}
                        className="absolute right-19 bottom-6 z-100 w-[min(360px,calc(100vw-112px))] rounded-lg border border-blue-200 bg-blue-50 text-blue-950 shadow-lg"
                    >
                        <div className="flex items-start gap-3">
                            <CircleHelp className="mt-0.5 size-5 shrink-0 text-blue-600" aria-hidden="true"/>
                            <div className="min-w-0 flex-1">
                                {/*<p className="text-sm font-semibold">页面操作说明</p>*/}
                                <p className="mt-1 text-xs leading-5">
                                    拖动画布可移动视图，滚轮可缩放；单击有下级的节点可展开或收起，双击节点可聚焦，双击空白区域可缩小视图；旗帜按钮用于展示
                                    gap 绝对值最大的平台路径。
                                </p>
                                <p className="mt-1 text-xs leading-5">
                                    Drag the canvas to move the view, and use the scroll wheel to zoom; clicking on a
                                    node with sub-nodes will expand or collapse them, double-clicking on a node will
                                    focus on it, and double-clicking on an empty area will reduce the view; the flag
                                    button is used to display the platform path with the largest absolute value of
                                    gap. </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Controls */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: 24,
                        right: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        zIndex: 100,
                        background: 'white',
                        padding: 6,
                        borderRadius: 12,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                    }}
                >
                    {[
                        {label: '+', title: 'Zoom In', action: () => reactFlowInstance.current?.zoomIn()},
                        {label: '\u2212', title: 'Zoom Out', action: () => reactFlowInstance.current?.zoomOut()},
                        {label: '\u25FB', title: 'Reset View', action: handleFitView},
                        {label: '\u25BE', title: 'Collapse All', action: handleCollapseAll},
                        {
                            label: '\u2691',
                            title: 'Highlight Only',
                            action: handleFilterCritical,
                            active: showHighlightPath,
                        },
                        {label: '\u26F6', title: 'Fullscreen', action: handleFullscreen},
                    ].map((btn, i) => (
                        <button
                            key={i}
                            onClick={btn.action}
                            title={btn.title}
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                border: 'none',
                                background: (btn as any).active ? '#fef2f2' : 'transparent',
                                color: (btn as any).active ? '#dc2626' : '#4a5568',
                                fontSize: 16,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontFamily: 'inherit',
                            }}
                        >
                            {btn.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onMouseEnter={() => setIsHelpVisible(true)}
                        onMouseLeave={() => setIsHelpVisible(false)}
                        onFocus={() => setIsHelpVisible(true)}
                        onBlur={() => setIsHelpVisible(false)}
                        title="Help"
                        aria-label="Help"
                        aria-describedby={isHelpVisible ? 'mindmap-help' : undefined}
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            border: 'none',
                            background: isHelpVisible ? '#eff6ff' : 'transparent',
                            color: isHelpVisible ? '#2563eb' : '#4a5568',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircleHelp size={18} aria-hidden="true"/>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function MindMapFlow() {
    return <MindMapFlowInner/>;
}
